// ProcessSelectionToolbar — the floating action bar that appears when ≥1 step
// row is multi-selected in the ProcessTree (P4 #28). A portaled, bottom-center
// glass bar (DESIGN-SYSTEM.md §1/§4/§6 — slate palette, au-panel-in entrance,
// z-overlay, shadow-lg) carrying the selection count + Delete / Copy / Paste.
//
// Pure view: it holds NO selection or model state — the host passes the count
// and the three action callbacks (each already funnels through the shared
// ProcessStepEditApi / clipboard helper). Rendered only when `count > 0`; the
// host clears the selection after Delete so the bar dismisses itself.
//
// Menu-taxonomy note (DESIGN-SYSTEM.md §5): Delete normally lives only in an
// object's context menu, never a toolbar. A MULTI-selection has no single
// context menu to host a "delete all N", so this transient bar is that surface;
// it is styled `danger` and shows the count, and disappears the instant the
// selection empties. Copy/Paste are non-destructive bulk actions with no other
// home.

import type { ReactElement } from "react";
import { createPortal } from "react-dom";
import { Button, IconButton } from "@archlang/ui";

export interface ProcessSelectionToolbarProps {
  /** How many rows are selected. The bar renders only when this is ≥ 1. */
  readonly count: number;
  /** Delete every selected step (→ deleteSteps → one journaled mutation). */
  readonly onDelete: () => void;
  /** Copy the selected steps' rendered lines to the clipboard. */
  readonly onCopy: () => void;
  /** Paste clipboard lines as new steps after the selection. Omitted / null
   *  when there's nothing to paste onto (no active anchor). */
  readonly onPaste?: (() => void) | null;
  /** Drop the selection (the × / Esc). */
  readonly onClear: () => void;
}

export function ProcessSelectionToolbar({
  count,
  onDelete,
  onCopy,
  onPaste,
  onClear,
}: ProcessSelectionToolbarProps): ReactElement | null {
  if (count < 1) return null;
  return createPortal(
    <div
      data-process-selection-toolbar=""
      role="toolbar"
      aria-label="Selected steps"
      className="au-panel-in z-overlay fixed bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/95 px-2.5 py-1.5 text-slate-200 shadow-lg backdrop-blur"
    >
      <span data-selection-count="" className="px-1 text-xs font-medium tabular-nums text-slate-300">
        {count} selected
      </span>
      <span className="h-4 w-px bg-slate-700" aria-hidden="true" />
      <Button variant="secondary" size="sm" data-selection-copy="" onClick={onCopy}>
        Copy
      </Button>
      <Button
        variant="secondary"
        size="sm"
        data-selection-paste=""
        onClick={() => onPaste?.()}
        disabled={!onPaste}
      >
        Paste
      </Button>
      <Button variant="danger" size="sm" data-selection-delete="" onClick={onDelete}>
        Delete
      </Button>
      <IconButton
        variant="ghost"
        size="sm"
        data-selection-clear=""
        aria-label="Clear selection"
        onClick={onClear}
      >
        ✕
      </IconButton>
    </div>,
    document.body,
  );
}
